import React, { useState, useEffect } from "react";
import { Modal, Button, Form, FormControl, InputGroup } from "react-bootstrap";
import { SECTION_RULES, SECTION_NAME_MAX, getRulesForRegion, BAND_TYPES } from "../../../lib/bands/bandModel";

// Usual first choice for a new section above / below the items: page-1-only
// details (addresses) above, something after the items (notes) below.
const DEFAULT_RULE = { top: "topFirstPage", bottom: "afterContent" };

// "Add section": a name, where it goes in the section order and when it
// prints. `sections` are the current sections in page order as
// { id, label, type }.
export default function AddSectionDialog({ show, sections, onAdd, onHide }) {
  const defaultAfter = sections.find((s) => s.id === "head")?.id || sections[0]?.id;
  const [name, setName] = useState("");
  const [after, setAfter] = useState(defaultAfter);
  const [rule, setRule] = useState(DEFAULT_RULE.top);
  const [height, setHeight] = useState(100);

  const itemsIndex = sections.findIndex((s) => s.type === BAND_TYPES.ITEMS);
  const afterIndex = sections.findIndex((s) => s.id === after);
  const region = itemsIndex >= 0 && afterIndex < itemsIndex ? "top" : "bottom";
  const rules = getRulesForRegion(region);

  // A new position may change which rules make sense.
  useEffect(() => {
    if (!rules.includes(rule)) setRule(DEFAULT_RULE[region]);
  }, [region]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (show) {
      setName("");
      setAfter(defaultAfter);
      setRule(DEFAULT_RULE.top);
      setHeight(100);
    }
  }, [show]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Modal show={show} onHide={onHide} centered backdropClassName="preview-modal-backdrop" style={{ zIndex: 10700 }}>
      <Modal.Header closeButton>
        <Modal.Title as="h5">Add section</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Group className="mb-3">
          <Form.Label className="fw-bold">Name</Form.Label>
          <FormControl
            value={name}
            placeholder="e.g. Addresses, Notes, Signature"
            maxLength={SECTION_NAME_MAX}
            onChange={(e) => setName(e.target.value)}
            autoFocus
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label className="fw-bold">Place it after</Form.Label>
          <select className="form-select" value={after} onChange={(e) => setAfter(e.target.value)}>
            {sections.map((s) => (
              <option key={s.id} value={s.id}>{s.label}</option>
            ))}
          </select>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label className="fw-bold">Prints</Form.Label>
          <select className="form-select" value={rules.includes(rule) ? rule : DEFAULT_RULE[region]} onChange={(e) => setRule(e.target.value)}>
            {rules.map((key) => (
              <option key={key} value={key}>{SECTION_RULES[key].label}</option>
            ))}
          </select>
        </Form.Group>

        <Form.Group style={{ width: "160px" }}>
          <Form.Label className="fw-bold">Height</Form.Label>
          <InputGroup>
            <FormControl type="number" min="10" value={height} onChange={(e) => setHeight(parseFloat(e.target.value) || 10)} />
            <InputGroup.Text>px</InputGroup.Text>
          </InputGroup>
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>Cancel</Button>
        <Button
          variant="primary"
          onClick={() => onAdd({ name: name.trim() || "New section", after, rule: rules.includes(rule) ? rule : DEFAULT_RULE[region], height: Math.max(10, height) })}
        >
          Add section
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
